import { useEffect, useState, type CSSProperties } from 'react';
import { IconBook2, IconChevronUp, IconExternalLink, IconPhoto, IconPencil } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import './i18n';
import { NEWS, NEWS_AUD, NEWS_CATS, type NewsAud } from './data';
import s from './news.module.css';

// Mint-Home · banner Tin nội bộ (1536: cao 260, pad 26/32, tiêu đề 28). Bấm "Đọc bài" / "Soạn tin" → toast (chưa có hộp đọc / soạn ở bản này).
export function News({ empty, compact = false }: { empty: boolean; compact?: boolean }) {
  const t = useT();
  const toast = useToast();
  const tip = (m: string) => toast({ msg: m });
  const [aud, setAud] = useState<NewsAud | 'all'>('all');
  const [cat, setCat] = useState(NEWS_CATS[0]!);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(true);
  const [min, setMin] = useState(false);
  const list = empty ? [] : NEWS.filter((n) => (aud === 'all' || n.aud.includes(aud)) && (cat === NEWS_CATS[0] || n.cat === cat));
  const i = list.length ? ((idx % list.length) + list.length) % list.length : 0;
  const cur = list[i];
  useEffect(() => {
    if (paused || list.length < 2) return;
    const t = window.setInterval(() => setIdx((x) => x + 1), 6000);
    return () => window.clearInterval(t);
  }, [paused, list.length]);
  const readK = cur?.album ? t('hn.nw.album') : t('hn.nw.read');
  const ReadIc = cur?.album ? IconPhoto : IconBook2;
  const style = { '--nc': cur?.c } as CSSProperties;
  if (compact) {
    return (
      <div className={s.mini} role="region" aria-label={t('hn.nw.aria')}>
        <span className={s.kick}>{t('hn.nw.kick')}</span>
        <b className={s.miniK}>{cur?.k ?? t('hn.nw.noNews')}</b>
        {cur && <span className={s.miniD}>{cur.when}</span>}
        <button type="button" className={`${s.sq} ${s.sq44}`} aria-label={t('hn.nw.next')} onClick={() => setIdx(i + 1)}>›</button>
      </div>
    );
  }
  if (min) {
    return (
      <div className={s.mini} role="region" aria-label={t('hn.nw.miniAria')}>
        <span className={s.kick}>{t('hn.nw.kick')}</span>
        <b className={s.miniK}>{cur?.k ?? t('hn.nw.noNews')}</b>
        <button type="button" className={s.sq} aria-label={t('hn.nw.next')} onClick={() => setIdx(i + 1)}>›</button>
        <button type="button" className={s.soft} onClick={() => tip(t('home.later'))}><ReadIc size={16} stroke={1.8} aria-hidden="true" />{readK}</button>
        <button type="button" className={s.softer} onClick={() => setMin(false)}><IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('hn.nw.openBanner')}</button>
      </div>
    );
  }
  return (
    <section className={`${s.hero} ${s.heroD}`} style={style} aria-label={t('hn.nw.aria')} aria-roledescription={t('hn.nw.carousel')}>
      <div className={s.art} aria-hidden="true">
        <svg width="100%" height="100%" viewBox="0 0 520 280" preserveAspectRatio="xMidYMid slice"><circle className={s.a1} cx="400" cy="70" r="150" /><circle className={s.a2} cx="250" cy="260" r="110" /><circle className={s.a3} cx="470" cy="250" r="64" /></svg>
        {cur && <span className={s.badge}>{cur.album ? t('hn.nw.photos', { n: cur.album }) : cur.cat}</span>}
      </div>
      <div className={s.body}>
        <div className={s.meta}>
          <span className={s.kick}>{t('hn.nw.kick')}</span>
          {cur && <span className={s.cat}>{cur.cat}</span>}
          {cur?.tags.map((g) => <button key={g} type="button" className={s.tag} onClick={() => tip(t('hn.nw.tagTip', { g }))}>#{g}</button>)}
        </div>
        {cur ? (
          <>
            <h2 className={s.h}>{cur.k}</h2>
            <p className={s.ex}>{cur.body[0]}</p>
            <div className={s.by}>{cur.by} · {cur.when} · {t('hn.nw.views', { n: cur.views })} · {t('hn.nw.sendTo')} {cur.aud.map((a) => NEWS_AUD.find((x) => x[0] === a)?.[1]).join(', ')}</div>
            <div className={s.grow} />
            <div className={s.ctl}>
              <button type="button" className={s.read} onClick={() => tip(t('home.later'))}><ReadIc size={16} stroke={1.8} aria-hidden="true" />{readK}</button>
              <button type="button" className={s.sq} aria-label={t('hn.nw.prev')} onClick={() => setIdx(i - 1)}>‹</button>
              <button type="button" className={s.sq} aria-label={paused ? t('hn.nw.play') : t('hn.nw.pause')} aria-pressed={paused} onClick={() => setPaused(!paused)}>{paused ? '▶' : 'II'}</button>
              <button type="button" className={s.sq} aria-label={t('hn.nw.next')} onClick={() => setIdx(i + 1)}>›</button>
              <div className={s.dots}>{list.map((n, j) => <button key={n.id} type="button" aria-label={t('hn.nw.item', { n: j + 1 })} className={j === i ? s.dOn : undefined} onClick={() => setIdx(j)} />)}</div>
              <span className={s.pos}>{i + 1} / {list.length}</span>
            </div>
          </>
        ) : <div className={s.none}>{t('hn.nw.none')}</div>}
      </div>
      <div className={s.tools}>
        <div className={s.aud} role="group" aria-label={t('hn.nw.audL')}>{NEWS_AUD.map(([k, l]) => <button key={k} type="button" aria-pressed={aud === k} onClick={() => { setAud(k); setIdx(0); }}>{l}</button>)}</div>
        <select className={s.sel} aria-label={t('hn.nw.catL')} value={cat} onChange={(e) => { setCat(e.target.value); setIdx(0); }}>{NEWS_CATS.map((c) => <option key={c}>{c}</option>)}</select>
        <button type="button" className={s.post} onClick={() => tip(t('home.later'))}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('hn.nw.write')}</button>
        <button type="button" className={s.fold} aria-label={t('hn.nw.fold')} title={t('hn.nw.fold')} onClick={() => setMin(true)}><IconChevronUp size={16} stroke={1.8} aria-hidden="true" /></button>
      </div>
    </section>
  );
}
